/**
 * Decay-fit comparison chart — EDT/T20/T30/Topt overlaid vs. frequency.
 * Paint layer only — see `../decayFits.ts` for the fit math and
 * `../decayFitChart.ts` for the geometry. Methods are told apart by LINE
 * STYLE (dash pattern), not colour — same ink-weight convention the web
 * dock uses (see `web/src/components/room/charts/decayFitDraw.ts`, which
 * this ports the METHOD_DASH/METHOD_WIDTH maps and legend layout from
 * verbatim, swapping its `canvasInk.stroke` for mobile's `INK.primary`).
 */

import { canvasTypeFont } from '@roomtreatment/design';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK } from './_shared';
import { computeRT60Fits } from '../decayFits';
import { computeDecayFitComparisonGeometry, DECAY_FIT_METHODS, type DecayFitMethod } from '../decayFitChart';
import type { ChartPad } from '../layout';
import type { RT60DecayCurve } from '../registry';

export const DECAY_FIT_PAD: ChartPad = { t: 10, r: 14, b: 20, l: 44 };

const METHOD_LABEL: Record<DecayFitMethod, string> = {
  edt: 'EDT', t20: 'T20', t30: 'T30', topt: 'Topt',
};
export const METHOD_DASH: Record<DecayFitMethod, number[]> = {
  edt: [2, 2],
  t20: [],
  t30: [6, 3],
  topt: [1, 3],
};
export const METHOD_WIDTH: Record<DecayFitMethod, number> = {
  edt: 1.4, t20: 2.0, t30: 1.4, topt: 1.4,
};

export interface DrawDecayFitComparisonOpts {
  decay: RT60DecayCurve;
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawDecayFitComparisonCanvas(canvas: HTMLCanvasElement, opts: DrawDecayFitComparisonOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const fits = computeRT60Fits(opts.decay);
  const geo = computeDecayFitComparisonGeometry({
    fits,
    width: opts.width,
    height: opts.height,
    pad: DECAY_FIT_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No decay curve yet — ≥200 Hz only');
    return;
  }

  // Grid deliberately flat (no major/minor distinction on either axis) —
  // matches web's decayFitDraw.ts, not a bug to "fix".
  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks, {
    gridMajor: INK.gridMinor,
    gridMinor: INK.gridMinor,
    xDistinguishMajor: false,
    yDistinguishMajor: false,
  });

  for (const series of geo.series) {
    drawSeriesLine(ctx, geo.frame, series.points, INK.primary, METHOD_WIDTH[series.method], METHOD_DASH[series.method]);
  }

  // Legend: one line-sample + label per method, top-left, inside the plot.
  ctx.font = canvasTypeFont('label');
  let lx = geo.frame.plotX + 6;
  const ly = geo.frame.plotY + 8;
  for (const method of DECAY_FIT_METHODS) {
    ctx.setLineDash(METHOD_DASH[method]);
    ctx.strokeStyle = INK.primary;
    ctx.lineWidth = METHOD_WIDTH[method];
    ctx.beginPath();
    ctx.moveTo(lx, ly);
    ctx.lineTo(lx + 14, ly);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = INK.label;
    ctx.textAlign = 'left';
    ctx.fillText(METHOD_LABEL[method], lx + 17, ly + 3);
    lx += 17 + ctx.measureText(METHOD_LABEL[method]).width + 10;
  }
}
